import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, WheelEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PeopleSliderProps } from './PeopleSlider.data';
import styles from './PeopleSlider.module.scss';
import { useDraggable } from '../../../hooks/useDraggable';
import Heading from '../../atoms/heading/Heading';
import PeopleSliderItem from './people-slider-item/PeopleSliderItem';
import gsap from 'gsap';
import type { PeopleSliderItemRef } from './people-slider-item/PeopleSliderItem.data';
import { useImageParallax } from '@/hooks/useImageParallax';
import useWindowSize from '../../../hooks/useWindowSize';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import type { ThemeColorProviderContextType } from '../../unlisted/ThemeColorProvider';
import { useThemeColorProviderContext } from '../../unlisted/ThemeColorProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import { isBackgroundDark } from '../../../util/color.utils';
import Icon from 'components/atoms/icon/Icon';

export default function PeopleSlider({ className, blok, ref }: PeopleSliderProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const sliderRef = useRef<HTMLDivElement>(null);
  const progressBarContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // If an image is missing we display none of the images. It will be displayed in grid instead
  const allImagesUploaded = blok.assets.every((asset) => asset?.image?.filename);
  const itemsCount = blok.assets.length;
  const draggableBoundsRef = useRef<HTMLDivElement>(null);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);

  const { itemsRef, setCurrentPosition } = useImageParallax(
    allImagesUploaded && !blok.no_inner_parallax,
    itemsCount,
  );
  const draggableInstance = useRef<Draggable | null>(null);

  const [figCaptionMaxHeight, setFigCaptionMaxHeight] = useState(0);

  const [isLeftArrowDisabled, setLeftArrowDisabled] = useState(true);
  const [isRightArrowDisabled, setRightArrowDisabled] = useState(false);

  const { lock, unLock } = useScrollProviderContext() as ScrollProviderContextType;

  const onDrag = useCallback(() => {
    if (!draggableInstance?.current) return;

    const { x, minX, maxX } = draggableInstance.current;
    const progress = gsap.utils.normalize(maxX, minX, x);

    setLeftArrowDisabled(x >= maxX);
    setRightArrowDisabled(x <= minX);

    if (progressBarRef.current) {
      gsap.set(progressBarRef.current, {
        left: `${100 * progress}%`,
        translateX: `${-100 * progress}%`,
      });
    }

    setCurrentPosition(Number(sliderRef?.current ? gsap.getProperty(sliderRef?.current, 'x') : 0));
  }, [draggableInstance, setCurrentPosition]);

  draggableInstance.current = useDraggable(
    sliderRef,
    {
      type: 'x',
      edgeResistance: 0.8,
      lockAxis: true,
      inertia: true,
      cursor: 'grab',
      onDrag,
      onThrowUpdate: onDrag,
    },
    {
      bounds: draggableBoundsRef,
    },
  );

  const boundWidth = draggableBoundsRef?.current?.getBoundingClientRect().width || 0;
  const sliderWidth = sliderRef?.current?.getBoundingClientRect().width || 0;

  if (draggableInstance.current) {
    if (boundWidth >= sliderWidth) {
      draggableInstance.current.disable();
    } else {
      draggableInstance.current.enable();
    }
  }

  const resizeProgressBar = () => {
    if (!progressBarRef.current || !progressBarContainerRef.current) return;

    const visibleBounds = draggableBoundsRef?.current?.getBoundingClientRect();
    const sliderBounds = sliderRef?.current?.getBoundingClientRect();

    if (visibleBounds && sliderBounds) {
      gsap.set(progressBarRef.current, {
        width: `${Math.min(1, visibleBounds.width / sliderBounds.width) * 100}%`,
      });
      gsap.set(progressBarContainerRef.current, {
        visibility: visibleBounds.width < sliderBounds.width ? 'visible' : 'hidden',
      });
    }
  };

  const { width: windowWidth } = useWindowSize();

  useEffect(resizeProgressBar, [
    draggableBoundsRef,
    sliderRef,
    progressBarRef,
    progressBarContainerRef,
    windowWidth,
  ]);

  useEffect(() => {
    let maxHeight = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const figCaptions = [...(elementRef.current?.querySelectorAll('#identityDetails') as any)];
    figCaptions.forEach((fc) => {
      maxHeight = Math.max(maxHeight, fc.clientHeight);
    });
    setFigCaptionMaxHeight(maxHeight);
  }, [elementRef]);

  const handleScroll = (e: WheelEvent) => {
    const absDeltaX = Math.abs(e.deltaX);
    const absDeltaY = Math.abs(e.deltaY);
    if (absDeltaX > 0 && absDeltaY < absDeltaX) {
      lock();
    } else {
      unLock();
    }

    if (draggableInstance.current) {
      const { minX, maxX } = draggableInstance.current;
      const currentX: number = parseInt(gsap.getProperty(sliderRef.current, 'x') as string, 10);
      const newX = currentX - e.deltaX;

      const x = Math.min(maxX, Math.max(minX, newX));
      setLeftArrowDisabled(x === maxX);
      setRightArrowDisabled(x === minX);
      gsap.set(sliderRef.current, { x });
      setCurrentPosition(Number(x));

      const progress = gsap.utils.normalize(maxX, minX, newX);

      if (progressBarRef.current) {
        gsap.set(progressBarRef.current, {
          left: `${100 * progress}%`,
          translateX: `${-100 * progress}%`,
        });
      }
    }
  };

  const gridGutterRef = useRef<HTMLDivElement>(null);
  const gapBetweenImagesRef = useRef<HTMLDivElement>(null);

  useThemeScrollBlock(elementRef, blok.use_accent_color);

  const { themeWrapperRef } = useThemeColorProviderContext() as ThemeColorProviderContextType;
  const [isDark, setIsDark] = useState<boolean | null>(null);

  const updateBackgroundTone = useCallback(() => {
    setIsDark(isBackgroundDark(elementRef.current));
  }, [elementRef]);

  useEffect(() => {
    updateBackgroundTone();

    const observerTarget = themeWrapperRef.current || document.documentElement;
    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(updateBackgroundTone);
    observer.observe(observerTarget, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      observer.disconnect();
    };
  }, [themeWrapperRef, updateBackgroundTone]);

  const emptyValues = {
    currentX: 0,
    minX: 0,
    maxX: 0,
    width: 0,
  };
  const getSliderValues = () => {
    if (!itemsRef?.current?.[0] || !draggableInstance.current) return emptyValues;
    const currentX = gsap.getProperty(sliderRef.current, 'x') as number;
    const imageWidth = gsap.getProperty(itemsRef.current[0].imageRef.current, 'width') as number;

    const gridGapWidth = gsap.getProperty(gapBetweenImagesRef.current, 'width') as number;
    const width = imageWidth + gridGapWidth;
    const { minX, maxX } = draggableInstance.current;

    return { currentX, minX, maxX, width };
  };

  const handleNext = () => {
    const { minX, currentX, width } = getSliderValues();
    const x = Math.max(
      minX,
      (Math.floor(currentX / width) - (currentX % width < 1 ? 1 : 0)) * width,
    );
    setRightArrowDisabled(x === minX);
    setLeftArrowDisabled(false);

    gsap.to(sliderRef.current, { x });
  };

  const handlePrevious = () => {
    const { maxX, currentX, width } = getSliderValues();
    const x = Math.min(
      maxX,
      (Math.ceil(currentX / width) + (currentX % width < 1 ? 1 : 0)) * width,
    );
    setLeftArrowDisabled(x === maxX);
    setRightArrowDisabled(false);

    gsap.to(sliderRef.current, { x });
  };

  const { minX, maxX } = getSliderValues();

  return (
    <div
      ref={elementRef}
      className={classNames(styles.peopleSlider, className)}
      {...sbEditable(blok)}
    >
      {/* The 2 divs below are here to get --grid-gutter and --grid-gap values in js */}
      <div className={styles.gridGutterWidth} ref={gridGutterRef} />
      <div className={styles.gapBetweenImages} ref={gapBetweenImagesRef} />

      <div className={classNames(styles.title, styles.flexContainer)}>
        {blok.title && (
          <Heading
            as="h3"
            size="heading-03"
            transitionInType="splitCharsSlideInUp"
            disableTriggerInOnScroll={false}
            className={styles.title}
          >
            {blok.title.split('\n').map((line, index) => (
              <div key={`${line}-${index}`}>{line}</div>
            ))}
          </Heading>
        )}

        {draggableInstance.current && maxX !== minX && (
          <div
            className={classNames(styles.buttonsContainer, {
              [styles.lightTheme]: !isDark,
              [styles.darkTheme]: isDark,
            })}
            ref={buttonsContainerRef}
          >
            <button
              className={styles.arrowButton}
              disabled={isLeftArrowDisabled}
              onClick={handlePrevious}
            >
              <Icon name="arrow-left-light" className={classNames(styles.icon)} />
            </button>

            <button
              className={styles.arrowButton}
              disabled={isRightArrowDisabled}
              onClick={handleNext}
            >
              <Icon name="arrow-right-light" className={classNames(styles.icon)} />
            </button>
          </div>
        )}

        {/* {allImagesUploaded && ( */}
        {/*   <div ref={progressBarContainerRef} className={styles.progressContainer}> */}
        {/*     <div ref={progressBarRef} className={styles.progressBar} /> */}
        {/*   </div> */}
        {/* )} */}
      </div>

      {allImagesUploaded ? (
        <div ref={draggableBoundsRef} className={styles.draggableBounds}>
          <div
            ref={sliderRef}
            className={styles.slider}
            onWheel={handleScroll}
            onMouseLeave={unLock}
          >
            {blok.assets.map((asset, index) => (
              <PeopleSliderItem
                key={asset._uid}
                ref={(ref) => {
                  if (itemsRef?.current?.[index] && ref) {
                    itemsRef.current[index] = ref as PeopleSliderItemRef;
                  }
                }}
                blok={asset}
                includeImage
                transitionInDelay={index * 0.15}
                figCaptionMaxHeight={figCaptionMaxHeight}
                viewTextLabel={blok.view_text_label}
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div ref={draggableBoundsRef} />
          <div
            className={classNames(styles.imageContainer, styles.threeColumn, {
              [styles.fourColumn]:
                itemsCount % 4 === 0 || (itemsCount % 3 !== 0 && itemsCount % 3 !== 2),
            })}
          >
            {blok.assets.map((asset, index) => (
              <PeopleSliderItem
                key={asset._uid}
                ref={(ref) => {
                  if (itemsRef?.current?.[index]) {
                    itemsRef.current[index] = ref as PeopleSliderItemRef;
                  }
                }}
                blok={asset}
                includeImage={false}
                transitionInDelay={index * 0.1}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
